/**
 * Units Step
 *
 * Distance unit preference selection (miles/kilometers).
 * Issue #1083 - Schema Naming & Distance Unit Standardization
 */

import { useState } from 'react';
import { Ruler, ChevronLeft, ArrowRight } from 'lucide-react';
import { OroLoader } from '../../OroLoader';
import { profileApi } from '@/lib/api/profile';
import { ApiError } from '@/lib/api';

interface UnitsStepProps {
  onNext: () => void;
  onBack: () => void;
}

export default function UnitsStep({ onNext, onBack }: UnitsStepProps) {
  const [selectedUnit, setSelectedUnit] = useState<'mi' | 'km'>('mi');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    setError(null);
    try {
      await profileApi.updateProfile({
        distance_unit: selectedUnit,
      });
      onNext();
    } catch (err) {
      // #2832: do NOT advance-and-lose on failure. The old code caught, logged,
      // and called onNext() anyway — the user's unit choice was silently
      // discarded with no signal. Surface a blocking, retryable error (matches
      // ProfileBasicsStep's doctrine) and stay on the step so Continue retries.
      const message =
        err instanceof ApiError
          ? err.message
          : 'Failed to save your unit preference. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Progress Bar - Step 5 of 7 */}
      <div className="shrink-0 px-6 pt-4">
        <div className="w-full h-1 bg-oro-surface-alt rounded-full overflow-hidden">
          <div
            className="h-full bg-oro-gold rounded-full transition-all duration-300"
            style={{ width: '71%' }}
          />
        </div>
        <p className="text-text-muted text-xs mt-2 text-center">Step 5 of 7</p>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        {/* Icon Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-oro-gold to-oro-gold-dark flex items-center justify-center shadow-lg mb-4 mx-auto">
            <Ruler className="w-8 h-8 text-black" />
          </div>
          <h2 id="onboarding-title" className="text-xl font-semibold text-text-primary mb-2">
            How do you measure distance?
          </h2>
          <p className="text-text-muted text-sm">
            Choose your preferred unit for event distances.
          </p>
        </div>

        {/* Unit Selection */}
        <form id="units-form" onSubmit={handleSubmit}>
          <div className="flex gap-4 justify-center">
            <button
              type="button"
              onClick={() => setSelectedUnit('mi')}
              className={`flex-1 max-w-[140px] py-6 rounded-xl font-medium text-lg transition-all ${
                selectedUnit === 'mi'
                  ? 'bg-oro-gold text-black shadow-lg ring-2 ring-oro-gold ring-offset-2 ring-offset-oro-surface'
                  : 'bg-oro-surface-alt border border-oro-border text-text-secondary hover:border-oro-border-hover'
              }`}
            >
              <div>Miles</div>
              <div className="text-xs mt-2 opacity-70">5K = 3.1 mi</div>
            </button>

            <button
              type="button"
              onClick={() => setSelectedUnit('km')}
              className={`flex-1 max-w-[140px] py-6 rounded-xl font-medium text-lg transition-all ${
                selectedUnit === 'km'
                  ? 'bg-oro-gold text-black shadow-lg ring-2 ring-oro-gold ring-offset-2 ring-offset-oro-surface'
                  : 'bg-oro-surface-alt border border-oro-border text-text-secondary hover:border-oro-border-hover'
              }`}
            >
              <div>Kilometers</div>
              <div className="text-xs mt-2 opacity-70">5K = 5 km</div>
            </button>
          </div>

          <p className="text-text-muted text-xs mt-6 text-center">
            You can change this anytime in your profile settings.
          </p>

          {/* Error Display (#2832 — blocking, retryable; does not advance) */}
          {error && (
            <div className="mt-4 p-4 bg-oro-error/10 border border-oro-error/20 rounded-xl" role="alert">
              <p className="text-sm text-oro-error">{error}</p>
            </div>
          )}
        </form>
      </div>

      {/* Bottom Actions */}
      <div className="shrink-0 p-4 border-t border-oro-border bg-oro-surface">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-3 rounded-lg bg-oro-surface-alt border border-oro-border text-text-primary hover:bg-oro-surface-alt/80 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="submit"
            form="units-form"
            disabled={isSubmitting}
            className={`flex-1 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-all ${
              !isSubmitting
                ? 'bg-oro-gold hover:bg-oro-gold/90 shadow-lg'
                : 'bg-oro-surface-alt border border-oro-border text-text-muted cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <>
                <OroLoader variant="inline" color="currentColor" className="text-black" decorative />
                <span className="text-black font-medium">Saving...</span>
              </>
            ) : (
              <>
                <span className="text-black font-medium">Continue</span>
                <ArrowRight className="w-5 h-5 text-black" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
